import React, { useState } from 'react';
import axios from 'axios';
import './App.css';

const App = () => {
  const [formData, setFormData] = useState({
    contact_data: {
      company: '',
      first_name: '',
      last_name: '',
      street: '',
      city: '',
      postal_code: '',
      country: '',
      email: ''
    },
    object_data: {
      project_name: '',
      street: '',
      city: '',
      postal_code: '',
      country: ''
    },
    probes: [
      {
        probe_number: {
          product_type: 'M',
          specification_code: '4020',
          year: '24',
          serial_number: '00001'
        },
        probe_length: ''
      }
    ]
  });

  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [errors, setErrors] = useState({});

  const PRODUCT_SPECS = {
    '3216': '32 x 2.9 mm PN16',
    '4016': '40 x 3.7 mm PN16',
    '4020': '40 x 4.5 mm PN20'
  };

  const handleInputChange = (section, field, value) => {
    setFormData(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [field]: value
      }
    }));
    // Clear error when user starts typing
    if (errors[`${section}.${field}`]) {
      setErrors(prev => ({
        ...prev,
        [`${section}.${field}`]: ''
      }));
    }
  };

  const handleProbeChange = (probeIndex, section, field, value) => {
    setFormData(prev => {
      const newProbes = [...prev.probes];
      if (section === 'probe_number') {
        newProbes[probeIndex] = {
          ...newProbes[probeIndex],
          probe_number: {
            ...newProbes[probeIndex].probe_number,
            [field]: value
          }
        };
      } else if (section === 'probe_length') {
        newProbes[probeIndex] = {
          ...newProbes[probeIndex],
          probe_length: value
        };
      } else {
        newProbes[probeIndex] = {
          ...newProbes[probeIndex],
          [field]: value
        };
      }
      return {
        ...prev,
        probes: newProbes
      };
    });
    
    // Clear error when user starts typing
    const errorKey = `probe_${probeIndex}.${section}.${field}`;
    if (errors[errorKey]) {
      setErrors(prev => ({
        ...prev,
        [errorKey]: ''
      }));
    }
  };

  const addProbe = () => {
    setFormData(prev => ({
      ...prev,
      probes: [
        ...prev.probes,
        {
          probe_number: {
            product_type: 'M',
            specification_code: '4020',
            year: '24',
            serial_number: '00001'
          },
          probe_length: ''
        }
      ]
    }));
  };

  const removeProbe = (probeIndex) => {
    if (formData.probes.length > 1) {
      setFormData(prev => ({
        ...prev,
        probes: prev.probes.filter((_, index) => index !== probeIndex)
      }));
      
      // Clear errors for removed probe
      setErrors(prev => {
        const newErrors = { ...prev };
        Object.keys(newErrors).forEach(key => {
          if (key.startsWith(`probe_${probeIndex}.`)) {
            delete newErrors[key];
          }
        });
        return newErrors;
      });
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    // Required fields
    if (!formData.contact_data.company) {
      newErrors['contact_data.company'] = 'Unternehmen ist erforderlich';
    }
    if (!formData.contact_data.email) {
      newErrors['contact_data.email'] = 'E-Mail ist erforderlich';
    }
    
    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (formData.contact_data.email && !emailRegex.test(formData.contact_data.email)) {
      newErrors['contact_data.email'] = 'Ungültige E-Mail-Adresse';
    }
    
    // Probe validation
    if (!formData.probes || formData.probes.length === 0) {
      newErrors['probes'] = 'Mindestens eine Sonde ist erforderlich';
    }
    
    formData.probes.forEach((probe, index) => {
      // Probe number validation
      if (!['M', 'S'].includes(probe.probe_number.product_type)) {
        newErrors[`probe_${index}.probe_number.product_type`] = 'Produkttyp muss M oder S sein';
      }
      if (!['3216', '4016', '4020'].includes(probe.probe_number.specification_code)) {
        newErrors[`probe_${index}.probe_number.specification_code`] = 'Unbekannter Spezifikationscode';
      }
      if (!/^\d{2}$/.test(probe.probe_number.year)) {
        newErrors[`probe_${index}.probe_number.year`] = 'Jahr muss zweistellig sein';
      }
      if (!/^\d{5}$/.test(probe.probe_number.serial_number)) {
        newErrors[`probe_${index}.probe_number.serial_number`] = 'Seriennummer muss fünfstellig sein';
      }
    });
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      setMessage('Bitte korrigieren Sie die Fehler im Formular.');
      return;
    }
    
    setIsLoading(true);
    setMessage('');
    
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/certificates/generate`,
        formData
      );
      
      if (response.data.success) {
        const certificateId = response.data.certificate_id;
        const emailAddress = response.data.email_address;
        const probeNumbers = response.data.probe_numbers;
        const totalProbes = response.data.total_probes;
        
        // Set success message with email info
        let successMsg;
        if (totalProbes === 1) {
          successMsg = `Werkszeugnis erfolgreich erstellt! Sondennummer: ${probeNumbers[0]}. Das Werkszeugnis wurde an ${emailAddress} versandt und wird automatisch heruntergeladen.`;
        } else {
          successMsg = `Werkszeugnisse für ${totalProbes} Sonden erfolgreich erstellt! Sondennummern: ${probeNumbers.join(', ')}. Die Werkszeugnisse wurden an ${emailAddress} versandt und werden automatisch heruntergeladen.`;
        }
        setMessage(successMsg);
        
        // Automatic download using direct method (not dependent on database)
        try {
          const downloadResponse = await axios.post(
            `${process.env.REACT_APP_BACKEND_URL}/api/certificates/download-direct`,
            formData,  // Send the same form data for direct PDF generation
            { responseType: 'blob' }
          );
          
          // Create download link
          const url = window.URL.createObjectURL(new Blob([downloadResponse.data]));
          const link = document.createElement('a');
          link.href = url;
          
          let filename;
          if (totalProbes === 1) {
            filename = `werkszeugnis_${probeNumbers[0].replace(/ /g, '_').toLowerCase()}.pdf`;
          } else {
            filename = `werkszeugnisse_${totalProbes}_sonden.pdf`;
          }
          
          link.setAttribute('download', filename);
          document.body.appendChild(link);
          link.click();
          link.remove();
          window.URL.revokeObjectURL(url);
          
        } catch (downloadError) {
          console.error('Direct download failed:', downloadError);
          // Fallback to database-based download
          try {
            const fallbackResponse = await axios.get(
              `${process.env.REACT_APP_BACKEND_URL}/api/certificates/${certificateId}/download`,
              { responseType: 'blob' }
            );
            
            const url = window.URL.createObjectURL(new Blob([fallbackResponse.data]));
            const link = document.createElement('a');
            link.href = url;
            
            let filename;
            if (totalProbes === 1) {
              filename = `werkszeugnis_${probeNumbers[0].replace(/ /g, '_').toLowerCase()}.pdf`;
            } else {
              filename = `werkszeugnisse_${totalProbes}_sonden.pdf`;
            }
            
            link.setAttribute('download', filename);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
            
          } catch (fallbackError) {
            console.error('Fallback download also failed:', fallbackError);
            setMessage(prev => prev + ' (Download-Fehler: Bitte kontaktieren Sie uns.)');
          }
        }
        
        // Reset form
        setFormData({
          contact_data: {
            company: '',
            first_name: '',
            last_name: '',
            street: '',
            city: '',
            postal_code: '',
            country: '',
            email: ''
          },
          object_data: {
            project_name: '',
            street: '',
            city: '',
            postal_code: '',
            country: ''
          },
          probes: [
            {
              probe_number: {
                product_type: 'M',
                specification_code: '4020',
                year: '24',
                serial_number: '00001'
              },
              probe_length: ''
            }
          ]
        });
      }
    } catch (error) {
      console.error('Error:', error);
      setMessage(error.response?.data?.detail || 'Fehler beim Erstellen des Werkszeugnisses');
    } finally {
      setIsLoading(false);
    }
  };

  const formatProbeNumber = (probe) => {
    const { product_type, specification_code, year, serial_number } = probe.probe_number;
    return `${product_type} ${specification_code} ${year} ${serial_number}`;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
      <div className="container mx-auto px-4 py-8">
        <div className="max-w-4xl mx-auto">
          <div className="bg-white rounded-xl shadow-lg p-8 mb-8">
            <h1 className="text-3xl font-bold text-center text-gray-800 mb-2">
              EWS-Werkszeugnis Generator
            </h1>
            <p className="text-center text-gray-600 mb-8">
              Automatisierter Generator für Werkszeugnisse von Erdwärmesonden nach EN 10204-2.2
            </p>
            
            <form onSubmit={handleSubmit} className="space-y-8">
              {/* Contact Data Section */}
              <div className="bg-gray-50 rounded-lg p-6">
                <h2 className="text-xl font-semibold text-gray-800 mb-4">
                  Kontaktdaten
                </h2>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Unternehmen *
                    </label>
                    <input
                      type="text"
                      value={formData.contact_data.company}
                      onChange={(e) => handleInputChange('contact_data', 'company', e.target.value)}
                      className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                        errors['contact_data.company'] ? 'border-red-500' : 'border-gray-300'
                      }`}
                      placeholder="Firmenname"
                    />
                    {errors['contact_data.company'] && (
                      <p className="text-red-500 text-sm mt-1">{errors['contact_data.company']}</p>
                    )}
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      E-Mail *
                    </label>
                    <input
                      type="email"
                      value={formData.contact_data.email}
                      onChange={(e) => handleInputChange('contact_data', 'email', e.target.value)}
                      className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                        errors['contact_data.email'] ? 'border-red-500' : 'border-gray-300'
                      }`}
                      placeholder="email@example.com"
                    />
                    {errors['contact_data.email'] && (
                      <p className="text-red-500 text-sm mt-1">{errors['contact_data.email']}</p>
                    )}
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Vorname
                    </label>
                    <input
                      type="text"
                      value={formData.contact_data.first_name}
                      onChange={(e) => handleInputChange('contact_data', 'first_name', e.target.value)}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="Vorname"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Name
                    </label>
                    <input
                      type="text"
                      value={formData.contact_data.last_name}
                      onChange={(e) => handleInputChange('contact_data', 'last_name', e.target.value)}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="Nachname"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Straße
                    </label>
                    <input
                      type="text"
                      value={formData.contact_data.street}
                      onChange={(e) => handleInputChange('contact_data', 'street', e.target.value)}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="Straße und Hausnummer"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      PLZ
                    </label>
                    <input
                      type="text"
                      value={formData.contact_data.postal_code}
                      onChange={(e) => handleInputChange('contact_data', 'postal_code', e.target.value)}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="Postleitzahl"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Ort
                    </label>
                    <input
                      type="text"
                      value={formData.contact_data.city}
                      onChange={(e) => handleInputChange('contact_data', 'city', e.target.value)}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="Stadt"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Land
                    </label>
                    <input
                      type="text"
                      value={formData.contact_data.country}
                      onChange={(e) => handleInputChange('contact_data', 'country', e.target.value)}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="Land"
                    />
                  </div>
                </div>
              </div>
              
              {/* Object Data Section */}
              <div className="bg-gray-50 rounded-lg p-6">
                <h2 className="text-xl font-semibold text-gray-800 mb-4">
                  Objektdaten
                </h2>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Objekt / Bauvorhaben
                    </label>
                    <input
                      type="text"
                      value={formData.object_data.project_name}
                      onChange={(e) => handleInputChange('object_data', 'project_name', e.target.value)}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="Projektname"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Straße
                    </label>
                    <input
                      type="text"
                      value={formData.object_data.street}
                      onChange={(e) => handleInputChange('object_data', 'street', e.target.value)}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="Straße und Hausnummer"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      PLZ
                    </label>
                    <input
                      type="text"
                      value={formData.object_data.postal_code}
                      onChange={(e) => handleInputChange('object_data', 'postal_code', e.target.value)}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="Postleitzahl"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Ort
                    </label>
                    <input
                      type="text"
                      value={formData.object_data.city}
                      onChange={(e) => handleInputChange('object_data', 'city', e.target.value)}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="Stadt"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Land
                    </label>
                    <input
                      type="text"
                      value={formData.object_data.country}
                      onChange={(e) => handleInputChange('object_data', 'country', e.target.value)}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="Land"
                    />
                  </div>
                </div>
              </div>
              
              {/* Probe Numbers Section */}
              <div className="bg-gray-50 rounded-lg p-6">
                <h2 className="text-xl font-semibold text-gray-800 mb-4">
                  Sondennummern ({formData.probes.length} Sonde{formData.probes.length !== 1 ? 'n' : ''})
                </h2>
                
                {formData.probes.map((probe, probeIndex) => (
                  <div key={probeIndex} className="mb-6 p-4 bg-white rounded-lg border-2 border-blue-100">
                    <div className="flex justify-between items-center mb-4">
                      <h3 className="text-lg font-medium text-gray-700">
                        Sonde {probeIndex + 1}
                      </h3>
                      {formData.probes.length > 1 && (
                        <button
                          type="button"
                          onClick={() => removeProbe(probeIndex)}
                          className="px-3 py-1 bg-red-500 text-white text-sm rounded hover:bg-red-600"
                        >
                          Entfernen
                        </button>
                      )}
                    </div>
                    
                    <div className="mb-4 p-4 bg-blue-50 rounded-lg">
                      <p className="text-sm text-gray-700 mb-2">
                        <strong>Aktuelle Sondennummer:</strong> {formatProbeNumber(probe)}
                      </p>
                      <p className="text-xs text-gray-500">
                        Format: Produkttyp Spezifikationscode Jahr Seriennummer
                      </p>
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Produkttyp
                        </label>
                        <select
                          value={probe.probe_number.product_type}
                          onChange={(e) => handleProbeChange(probeIndex, 'probe_number', 'product_type', e.target.value)}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        >
                          <option value="M">M</option>
                          <option value="S">S</option>
                        </select>
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Spezifikationscode
                        </label>
                        <select
                          value={probe.probe_number.specification_code}
                          onChange={(e) => handleProbeChange(probeIndex, 'probe_number', 'specification_code', e.target.value)}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        >
                          {Object.entries(PRODUCT_SPECS).map(([code, description]) => (
                            <option key={code} value={code}>
                              {code} - {description}
                            </option>
                          ))}
                        </select>
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Jahr (2-stellig)
                        </label>
                        <input
                          type="text"
                          value={probe.probe_number.year}
                          onChange={(e) => handleProbeChange(probeIndex, 'probe_number', 'year', e.target.value)}
                          className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                            errors[`probe_${probeIndex}.probe_number.year`] ? 'border-red-500' : 'border-gray-300'
                          }`}
                          placeholder="24"
                          maxLength="2"
                        />
                        {errors[`probe_${probeIndex}.probe_number.year`] && (
                          <p className="text-red-500 text-sm mt-1">{errors[`probe_${probeIndex}.probe_number.year`]}</p>
                        )}
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Seriennummer (5-stellig)
                        </label>
                        <input
                          type="text"
                          value={probe.probe_number.serial_number}
                          onChange={(e) => handleProbeChange(probeIndex, 'probe_number', 'serial_number', e.target.value)}
                          className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                            errors[`probe_${probeIndex}.probe_number.serial_number`] ? 'border-red-500' : 'border-gray-300'
                          }`}
                          placeholder="00001"
                          maxLength="5"
                        />
                        {errors[`probe_${probeIndex}.probe_number.serial_number`] && (
                          <p className="text-red-500 text-sm mt-1">{errors[`probe_${probeIndex}.probe_number.serial_number`]}</p>
                        )}
                      </div>
                    </div>
                    
                    <div className="mt-4">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Sondenlänge (optional)
                      </label>
                      <input
                        type="text"
                        value={probe.probe_length}
                        onChange={(e) => handleProbeChange(probeIndex, 'probe_length', '', e.target.value)}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="z.B. 100m"
                      />
                    </div>
                  </div>
                ))}
                
                {/* Add Probe Button */}
                <button
                  type="button"
                  onClick={addProbe}
                  className="w-full py-3 px-4 border-2 border-dashed border-blue-300 rounded-lg text-blue-600 hover:border-blue-500 hover:bg-blue-50 transition-colors font-medium"
                >
                  + Weitere Sonde hinzufügen
                </button>
              </div>
              
              {/* Submit Button */}
              <div className="flex justify-center">
                <button
                  type="submit"
                  disabled={isLoading}
                  className={`px-8 py-3 rounded-lg font-semibold text-white transition-colors ${
                    isLoading
                      ? 'bg-gray-400 cursor-not-allowed'
                      : 'bg-blue-600 hover:bg-blue-700 focus:ring-2 focus:ring-blue-500'
                  }`}
                >
                  {isLoading ? 'Wird erstellt...' : 'Werkszeugnis erstellen'}
                </button>
              </div>
            </form>
            
            {/* Message Display */}
            {message && (
              <div className={`mt-6 p-4 rounded-lg ${
                message.includes('erfolgreich') ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
              }`}>
                {message}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default App;